import { useState, useRef, useEffect } from 'react'
import {
  Box,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  Paper,
  Fade,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious'
import CloseIcon from '@mui/icons-material/Close'
import QueueMusicIcon from '@mui/icons-material/QueueMusic'
import RepeatIcon from '@mui/icons-material/Repeat'
import RepeatOneIcon from '@mui/icons-material/RepeatOne'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { usePlaylistAudio } from '../audio/PlaylistAudioContext'
import { getSurahInfo, getLocalizedSurahName } from '../lib/surahs'
import { VolumeControl } from './VolumeControl'

/**
 * Compact, FAB-style mini player anchored to the right edge above the
 * FeedbackFab. Replaces the previous full-width bottom bar which:
 *   (a) overlapped with the reader's own AudioPlayer when both were visible
 *   (b) led some users to believe they were still on the playlist page
 *       after navigating away (the wide bar visually dominated the bottom).
 *
 * Two visual states:
 *   - collapsed (default): a single 56px circular pill with a soft pulse
 *     while playing, anchored at `right: 16, bottom: <above-FeedbackFab>`.
 *   - expanded: a small floating panel with surah/playlist labels, transport
 *     controls (prev / play / next / repeat), progress, and close.
 *
 * Hidden when:
 *   - no playlist is bound (idle)
 *   - the user is on the active playlist's detail page (which has its own
 *     full player)
 */
export function PlaylistMiniPlayer() {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation()

  const {
    activePlaylist,
    isPlaying,
    activeSurahNumber,
    currentAyahNumber,
    currentTime,
    duration,
    repeatMode,
    cycleRepeatMode,
    togglePlay,
    nextSurah,
    prevSurah,
    setActivePlaylist,
    volume,
    setVolume,
  } = usePlaylistAudio()

  const [expanded, setExpanded] = useState(false)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const bubbleRef = useRef<HTMLDivElement | null>(null)

  // Outside-click closes the expanded panel. Bound only while open so the
  // listener doesn't sit attached forever in the collapsed state.
  useEffect(() => {
    if (!expanded) return
    const handle = (e: MouseEvent) => {
      const t = e.target as Node
      if (panelRef.current?.contains(t)) return
      if (bubbleRef.current?.contains(t)) return
      setExpanded(false)
    }
    window.addEventListener('mousedown', handle)
    return () => window.removeEventListener('mousedown', handle)
  }, [expanded])

  // Hide on the playlist's own detail page (it has its own large player).
  const isOnActivePlaylistPage =
    activePlaylist !== null && location.pathname === `/playlists/${activePlaylist.id}`
  if (!activePlaylist || isOnActivePlaylistPage) return null

  const info = activeSurahNumber !== null ? getSurahInfo(activeSurahNumber) : null
  const surahLabel = info
    ? `${activeSurahNumber}. ${getLocalizedSurahName(activeSurahNumber!, i18n.language)}`
    : activeSurahNumber !== null
    ? `${t('playlists.surahFallback', { number: activeSurahNumber })}`
    : t('playlists.playlist')
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  const repeatIcon =
    repeatMode === 'repeat-one' ? (
      <RepeatOneIcon fontSize="small" />
    ) : (
      <RepeatIcon fontSize="small" />
    )
  const repeatLabel =
    repeatMode === 'off'
      ? t('audio.repeatOff')
      : repeatMode === 'repeat-all'
      ? t('audio.repeatAll')
      : t('audio.repeatOne')

  const goToPlaylist = () => {
    setExpanded(false)
    navigate(`/playlists/${activePlaylist.id}`)
  }

  // Vertical stacking math (right edge, bottom-up):
  //   1. AudioPlayer (when visible)  bottom: 16/64, height ≤ 56
  //   2. FeedbackFab                 bottom: 16/90  + AudioPlayer lift
  //   3. MiniPlayer (this)           bottom: FeedbackFab bottom + 56 + gap
  //
  // The lift values mirror the ones inside components/FeedbackFab.tsx so the
  // three corner elements never collide. We're already hidden on the active
  // playlist's own detail page (it has its own large player), so we only need
  // to handle the /reader/* lift here.
  //
  // Reader-page exception (mobile only): ReaderFloatingSettings docks a
  // 5-button vertical rail (~256px tall, 5 × 44px + 4 × 6px gaps + padding)
  // at `right: 12, bottom: 140` on mobile. Without compensation our FAB
  // would land in the middle of that rail. Push above the rail's TOP edge
  // by adding its full footprint + a gap on the reader page.
  const FAB_GAP = 12
  const FAB_HEIGHT = 56
  const AUDIO_PLAYER_HEIGHT = 56
  const FLOATING_RAIL_HEIGHT = 280 // generous estimate above 5×44 + spacing
  const isOnReaderPage = location.pathname.startsWith('/reader')
  const audioPlayerLift = isOnReaderPage ? AUDIO_PLAYER_HEIGHT + FAB_GAP : 0
  const readerMobileBoost = isMobile && isOnReaderPage ? FLOATING_RAIL_HEIGHT : 0
  const feedbackFabBottomNum = (isMobile ? 90 : 16) + audioPlayerLift
  const baseBottomNum = feedbackFabBottomNum + FAB_HEIGHT + FAB_GAP + readerMobileBoost
  const baseBottom = isMobile
    ? `calc(${baseBottomNum}px + var(--safe-bottom, 0px))`
    : baseBottomNum

  return (
    <>
      {/* Collapsed bubble: always rendered when there's an active playlist.
          Hidden visually under the expanded panel's `aria-hidden` semantics. */}
      <Tooltip
        title={expanded ? t('common.close') : t('playlists.miniPlayerLabel')}
        placement="left"
        disableHoverListener={expanded}
      >
        <Box
          ref={bubbleRef}
          onClick={() => setExpanded((v) => !v)}
          role="button"
          aria-label={t('playlists.miniPlayerLabel')}
          aria-expanded={expanded}
          sx={{
            position: 'fixed',
            // += safe-right so the bubble clears a landscape notch/rounded
            // corner on the right edge. Inert (16px, same as before) when
            // the inset is 0.
            right: 'calc(16px + var(--safe-right, 0px))',
            bottom: baseBottom,
            width: FAB_HEIGHT,
            height: FAB_HEIGHT,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            zIndex: theme.zIndex.drawer + 3,
            boxShadow: isDark
              ? '0 6px 18px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.35)'
              : '0 6px 16px rgba(15,118,110,0.3), 0 2px 6px rgba(0,0,0,0.08)',
            transition: 'transform 0.18s ease, box-shadow 0.18s ease',
            '&:hover': {
              transform: 'scale(1.06)',
            },
            // Soft pulse when actively playing. Honors reduced-motion via the
            // global CSS preference (the keyframe simply doesn't render in
            // user-agents that respect it).
            ...(isPlaying && {
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: -4,
                borderRadius: '50%',
                border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                animation: 'qlearnMiniPulse 1.6s ease-out infinite',
              },
              '@keyframes qlearnMiniPulse': {
                '0%': { transform: 'scale(1)', opacity: 0.7 },
                '100%': { transform: 'scale(1.35)', opacity: 0 },
              },
              '@media (prefers-reduced-motion: reduce)': {
                '&::after': { animation: 'none' },
              },
            }),
          }}
        >
          <QueueMusicIcon sx={{ fontSize: 26 }} />
          {/* Tiny play-state dot in the bottom-right of the bubble */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 6,
              right: 6,
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: 'background.paper',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${theme.palette.divider}`,
            }}
            aria-hidden
          >
            {isPlaying ? (
              <PauseIcon sx={{ fontSize: 8 }} />
            ) : (
              <PlayArrowIcon sx={{ fontSize: 8 }} />
            )}
          </Box>
        </Box>
      </Tooltip>

      {/* Expanded panel: anchored to the bubble's left side, slides in. */}
      <Fade in={expanded} timeout={180} mountOnEnter unmountOnExit>
        <Paper
          ref={panelRef}
          elevation={0}
          role="region"
          aria-label={t('playlists.miniPlayerLabel')}
          sx={{
            position: 'fixed',
            // sit just left of the bubble; += safe-right so it clears a
            // landscape notch/rounded corner on the right edge. `width`
            // subtracts the same inset at every breakpoint so the panel's
            // LEFT edge stays fixed instead of drifting off-screen —
            // without this, widening `right` alone would shift the whole
            // panel left by the inset while its width stayed the same.
            // Both branches matter: real notched phones in landscape report
            // CSS viewport widths >= ~667px, i.e. the `sm` (fixed 340)
            // branch, not `xs` — so `sm` needs the same compensation as
            // `xs` for the mitigation to actually reach its target devices.
            right: `calc(${16 + FAB_HEIGHT + 8}px + var(--safe-right, 0px))`,
            bottom: baseBottom,
            width: {
              xs: `calc(100vw - ${16 + FAB_HEIGHT + 24}px - var(--safe-right, 0px))`,
              sm: 'calc(340px - var(--safe-right, 0px))',
            },
            maxWidth: 360,
            zIndex: theme.zIndex.drawer + 3,
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            backdropFilter: 'blur(28px) saturate(180%)',
            WebkitBackdropFilter: 'blur(28px) saturate(180%)',
            background: isDark
              ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.96)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.96)} 0%, ${alpha(theme.palette.background.paper, 0.92)} 100%)`,
            boxShadow: isDark
              ? '0 16px 40px rgba(0,0,0,0.6), 0 4px 10px rgba(0,0,0,0.35)'
              : '0 12px 32px rgba(15,118,110,0.14), 0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          <Stack sx={{ p: 1.5 }} spacing={1}>
            {/* Title row — click jumps back to playlist detail */}
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', cursor: 'pointer' }}
              onClick={goToPlaylist}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                }}
              >
                <QueueMusicIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                  {surahLabel}
                  {currentAyahNumber !== null && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 0.5 }}
                    >
                      · {currentAyahNumber}
                    </Typography>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {activePlaylist.name}
                </Typography>
              </Box>
              <Tooltip title={t('common.close')}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    setActivePlaylist(null)
                  }}
                  aria-label={t('common.close')}
                  sx={{ color: 'text.secondary' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Progress ribbon */}
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            />

            {/* Transport */}
            <Stack
              direction="row"
              sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}
            >
              <Tooltip title={repeatLabel}>
                <IconButton
                  size="small"
                  onClick={cycleRepeatMode}
                  aria-label={repeatLabel}
                  sx={{
                    color: repeatMode === 'off' ? 'text.disabled' : 'primary.main',
                  }}
                >
                  {repeatIcon}
                </IconButton>
              </Tooltip>

              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <Tooltip title={t('audio.previous')}>
                  <IconButton size="small" onClick={prevSurah} aria-label={t('audio.previous')}>
                    <SkipPreviousIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={isPlaying ? t('audio.pause') : t('audio.play')}>
                  <IconButton
                    onClick={togglePlay}
                    aria-label={isPlaying ? t('audio.pause') : t('audio.play')}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      width: 40,
                      height: 40,
                      '&:hover': { bgcolor: 'primary.dark' },
                    }}
                  >
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('audio.next')}>
                  <IconButton size="small" onClick={nextSurah} aria-label={t('audio.next')}>
                    <SkipNextIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
                <VolumeControl volume={volume} onVolumeChange={setVolume} />
                <Tooltip title={t('playlists.openPlaylist')}>
                  <IconButton
                    size="small"
                    onClick={goToPlaylist}
                    aria-label={t('playlists.openPlaylist')}
                    sx={{ color: 'text.secondary' }}
                  >
                    <QueueMusicIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Fade>
    </>
  )
}
